import { BookOpen, FileText, Globe, HelpCircle, Package, StickyNote } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usedAiSources } from './usedAiSources';

const SOURCE_META: Record<string, { label: string; Icon: typeof FileText }> = {
  faq: { label: 'FAQ', Icon: HelpCircle },
  document: { label: 'Doc', Icon: FileText },
  website: { label: 'Website', Icon: Globe },
  note: { label: 'Note', Icon: StickyNote },
  product: { label: 'Product', Icon: Package },
};

// 'bubble' sits on the blue AI message bubble; 'card' sits on the light draft card.
const TONES = {
  bubble: {
    details: 'mt-1 px-1 group',
    summary: 'text-blue-100/90 hover:text-white',
    chip: 'bg-white/15 text-white',
  },
  card: {
    details: 'group',
    summary: 'text-muted-foreground hover:text-foreground',
    chip: 'border bg-background text-foreground',
  },
};

/**
 * The sources an AI reply or draft used, folded behind "N sources". The same
 * list shows on AI messages and on drafts, so staff can check where a draft's
 * facts came from before they send it.
 */
export function AiSources({
  aiMeta,
  tone = 'bubble',
}: {
  aiMeta?: { sources?: unknown } | null;
  tone?: keyof typeof TONES;
}) {
  const sources = usedAiSources(aiMeta);
  if (sources.length === 0) return null;
  const styles = TONES[tone];

  return (
    <details className={styles.details}>
      <summary
        className={cn(
          'flex items-center gap-1 cursor-pointer list-none text-[10px] font-medium',
          styles.summary,
        )}
      >
        <BookOpen className="size-3" />
        {`${sources.length} ${sources.length === 1 ? 'source' : 'sources'}`}
      </summary>
      <div className="mt-1 flex flex-wrap gap-1">
        {sources.map((source, index) => {
          const meta = SOURCE_META[source.type ?? ''] || { label: 'Source', Icon: FileText };
          const { Icon } = meta;
          // A source with a page opens it in a new tab.
          const Wrapper = source.url ? 'a' : 'span';
          const link = source.url
            ? {
                href: source.url,
                target: '_blank',
                rel: 'noopener noreferrer',
                className: 'no-underline',
              }
            : {};
          return (
            <Wrapper key={source.refId || `${source.type}-${index}`} {...link}>
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] max-w-[260px]',
                  styles.chip,
                )}
              >
                <Icon className="size-3 shrink-0" />
                <span className="opacity-80">{meta.label}</span>
                <span className="truncate">{source.title}</span>
              </span>
            </Wrapper>
          );
        })}
      </div>
    </details>
  );
}
